import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { Button, FullTable, ModalDefault, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, formatDateTime } from "@components/ow";
import { declarationAuthor } from "@helpers/declarations";

// Declarações, no que a visão da empresa (/c/declarations) e a do prestador
// (/e/declarations) compartilham: a tabela paginada pelo back e o modal de ver.
// A data vem do `created_at_iso` (o `created_at` é o texto da tela antiga, no fuso
// do servidor); sem ele (back antigo), "—".

export const PAGE_SIZE = 25;

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

// Texto em até 2 linhas; o inteiro fica no title e no modal
const Clamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// O texto como foi digitado, com as quebras de linha
const Message = styled.div`
  white-space: pre-line;
  overflow-wrap: anywhere;
`;

const isAnonymous = (declaration) => declarationAuthor(declaration) === "Anônima";

/**
 * Colunas da lista. `own`: a lista do próprio prestador, em que a assinatura é
 * "Com seu nome" ou "Anônima" no lugar do nome.
 */
export function declarationColumns({ own = false } = {}) {
  return [
    {
      id: "type",
      header: "Tipo",
      size: 140,
      cell: ({ row }) => <Text $text={escapeHtml(row.original.type || "-")} $variant="paragraph_small_bold" $color="N900" />,
    },
    {
      id: "text",
      header: "Declaração",
      size: 560,
      cell: ({ row }) => (
        <Clamp title={row.original.text || ""}>
          <Text $text={escapeHtml(row.original.text || "-")} $variant="paragraph_small_medium" $color="N900" />
        </Clamp>
      ),
    },
    {
      id: "author",
      header: own ? "Assinatura" : "Enviada por",
      size: 200,
      cell: ({ row }) => {
        const anonymous = isAnonymous(row.original);
        const text = anonymous ? "Anônima" : own ? "Com seu nome" : declarationAuthor(row.original);
        return <Text $text={escapeHtml(text)} $variant="paragraph_small_medium" $color={anonymous ? "N400" : "N900"} />;
      },
    },
    { id: "created_at", header: "Enviada em", size: 180, cell: ({ row }) => formatDateTime(row.original.created_at_iso) },
  ];
}

/**
 * Lista paginada pelo back (25 por página). Para trocar o filtro ou recarregar,
 * quem usa muda a `key`: a tabela volta para a primeira página.
 *
 * Props:
 * - query: filtro extra do GET /declarations (ex.: "myList=1", "type=Elogio")
 * - columns: as de `declarationColumns`
 * - noResults: o `NoResults` da lista vazia
 * - onOpen(declaration): clique na linha
 */
export function DeclarationsTable({ query = "", columns, noResults, onOpen }) {
  const declarationsService = useService("declarations");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (trocas de página seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);

  useEffect(() => {
    fetchDeclarations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize]);

  async function fetchDeclarations() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const params = [query, `page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`].filter(Boolean).join("&");
      const result = await declarationsService.list(params);
      if (request !== lastRequest.current) return;
      if (!result?.data) throw new Error("Erro ao carregar as declarações");
      setPaginationData(result.data);
    } catch (err) {
      if (request !== lastRequest.current) return;
      toast.error(err?.response?.data?.msg || err?.message || "Erro ao carregar as declarações");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  return (
    <FullTable
      data={paginationData.list}
      columns={columns}
      rowCount={paginationData.totalRegisters}
      pagination={pagination}
      setPagination={setPagination}
      getRowId={(row) => String(row.id)}
      isLoading={loading}
      onRowClick={onOpen}
      noResults={noResults}
    />
  );
}

/** A declaração inteira: o tipo no título, o texto e quem enviou e quando. */
export function DeclarationViewModal({ declaration, own = false, onClose }) {
  const anonymous = isAnonymous(declaration);
  const date = formatDateTime(declaration.created_at_iso);
  const author = anonymous ? "Anônima" : own ? "Com seu nome" : declarationAuthor(declaration);

  return (
    <ModalDefault
      $showModal
      $setShowModal={onClose}
      $size="medium"
      $title={escapeHtml(declaration.type || "Declaração")}
      $contentBackground="N0"
      $footer={<Button $text="Fechar" $variant="outline" $action={onClose} $noWidth />}
    >
      <Body>
        <Message>
          <Text $text={escapeHtml(declaration.text || "")} $variant="paragraph_small_medium" $color="N900" />
        </Message>
        <Text
          $text="%s · enviada em %s"
          $replaces={[escapeHtml(author), date]}
          $variant="caption_medium"
          $color="N400"
          $colorEmphasis="N400"
        />
      </Body>
    </ModalDefault>
  );
}
